import { useCallback, useContext, useEffect, useState } from 'react'
import PrimaryButton from '../../../global/components/buttons/primary-button/PrimaryButton'
import SecondaryButton from '../../../global/components/buttons/secondary-button/SecondaryButton'
import { ToastNotificationContext } from '../../../global/context/toast-context/ToastNotificationContext'
import { fetchData } from '../../../global/utils/fetch'
import {
  getCategories,
  getEvaluationQuestions,
  deleteEvaluationQuestionApi,
  runFullTests as runFullTestsApi,
} from '../../api'
import { CategoryListItem } from '../category-table/CategoryTable'
import EvaluationTable, { EvaluationQuestionItem } from '../evaluation-table/EvaluationTable'
import EvaluationModal from '../evaluation-modal/EvaluationModal'
import TestResultsTable, { CategoryTestResultItem } from '../test-results-table/TestResultsTable'
import styles from './EvaluationsContent.module.css'

function EvaluationsContent() {
  const { toastStatus, setToastStatus } = useContext(ToastNotificationContext)

  const [categories, setCategories] = useState<CategoryListItem[]>([])
  const [isCategoriesLoading, setIsCategoriesLoading] = useState(true)
  const [evaluationQuestions, setEvaluationQuestions] = useState<EvaluationQuestionItem[]>([])
  const [isEvalLoading, setIsEvalLoading] = useState(true)
  const [isEvalModalOpen, setIsEvalModalOpen] = useState(false)
  const [testResults, setTestResults] = useState<CategoryTestResultItem[]>([])
  const [testSummary, setTestSummary] = useState<{
    total: number
    passed: number
    failed: number
    errorCount: number
    pipelineTestsRun?: number
    pipelineTestsPassed?: number
  } | null>(null)
  const [isRunningTests, setIsRunningTests] = useState(false)

  const loadCategories = useCallback(async () => {
    setIsCategoriesLoading(true)
    try {
      const data = await fetchData<CategoryListItem[]>(getCategories())
      setCategories(data)
    } catch (error: any) {
      setToastStatus({
        ...toastStatus,
        isOpen: true,
        message: error.message || 'Failed to load categories.',
        severity: 'error',
      })
    } finally {
      setIsCategoriesLoading(false)
    }
  }, [])

  const loadEvaluationQuestions = useCallback(async () => {
    setIsEvalLoading(true)
    try {
      const data = await fetchData<EvaluationQuestionItem[]>(getEvaluationQuestions())
      setEvaluationQuestions(data)
    } catch (error: any) {
      setToastStatus({
        ...toastStatus,
        isOpen: true,
        message: error.message || 'Failed to load evaluation questions.',
        severity: 'error',
      })
    } finally {
      setIsEvalLoading(false)
    }
  }, [])

  useEffect(() => {
    loadCategories()
    loadEvaluationQuestions()
  }, [loadCategories, loadEvaluationQuestions])

  async function handleDeleteEvaluationQuestion(objectId: string) {
    if (!window.confirm('Are you sure you want to delete this evaluation question?')) return
    try {
      await fetchData(deleteEvaluationQuestionApi(objectId))
      setToastStatus({
        ...toastStatus,
        isOpen: true,
        message: 'Evaluation question deleted.',
        severity: 'success',
      })
      loadEvaluationQuestions()
    } catch (error: any) {
      setToastStatus({
        ...toastStatus,
        isOpen: true,
        message: error.message || 'Failed to delete evaluation question.',
        severity: 'error',
      })
    }
  }

  async function handleRunTests() {
    setIsRunningTests(true)
    setTestResults([])
    setTestSummary(null)
    try {
      const data = await fetchData<{
        results: CategoryTestResultItem[]
        summary: {
          total: number; passed: number; failed: number; errorCount: number
          pipelineTestsRun: number; pipelineTestsPassed: number
        }
      }>(runFullTestsApi())
      setTestResults(data.results)
      setTestSummary(data.summary)
      setToastStatus({
        ...toastStatus,
        isOpen: true,
        message: `Tests complete: ${data.summary.passed}/${data.summary.total} passed.`,
        severity: 'success',
      })
    } catch (error: any) {
      setToastStatus({
        ...toastStatus,
        isOpen: true,
        message: error.message || 'Failed to run tests.',
        severity: 'error',
      })
    } finally {
      setIsRunningTests(false)
    }
  }

  return (
    <div className={styles.container}>
      <div className={styles.section}>
        <div className={styles.sectionHeader}>
          <h2 className={styles.sectionTitle}>Test Suite</h2>
          <div className={styles.sectionActions}>
            <SecondaryButton
              buttonName={isRunningTests ? 'Running...' : 'Run All Tests'}
              onClick={handleRunTests}
              disabled={isRunningTests || evaluationQuestions.length === 0 || categories.length === 0}
            />
            <PrimaryButton
              buttonName='Add Question'
              onClick={() => setIsEvalModalOpen(true)}
            />
          </div>
        </div>
        <div className={styles.sectionContent}>
          {categories.length === 0 && !isCategoriesLoading && (
            <p className={styles.hint}>Create a category first to enable testing.</p>
          )}
          <div className={styles.questionsWrapper}>
            <EvaluationTable
              questions={evaluationQuestions}
              isLoading={isEvalLoading}
              onDelete={handleDeleteEvaluationQuestion}
            />
          </div>
          {testResults.length > 0 && (
            <TestResultsTable
              results={testResults}
              isLoading={isRunningTests}
              summary={testSummary}
            />
          )}
        </div>
        <EvaluationModal
          isOpen={isEvalModalOpen}
          onClose={() => setIsEvalModalOpen(false)}
          onSaveComplete={loadEvaluationQuestions}
        />
      </div>
    </div>
  )
}

export default EvaluationsContent
